import { toZonedTime } from 'date-fns-tz';

import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import AuditOutlined from '@ant-design/icons/lib/icons/AuditOutlined';
import TableOutlined from '@ant-design/icons/TableOutlined';
import { minutesToHours } from '../../domain/common/timeHelpers';
import { toUniversalTime } from '../../domain/event';
import { IEvent, ISlice, OptType, SignalType } from '../../domain/event/interface';
import { IResource } from '../../domain/event/resource';
import dayjs from 'dayjs';
import { Actor } from 'src/domain/auth/actor';
import FileDoneOutlined from '@ant-design/icons/lib/icons/FileDoneOutlined';

export enum VIEW_MODE {
    HISTORY = 'history',
    CALENDAR = 'calendar',
    TRACKING = 'tracking',
    COMPANY_PARTICIPATION = 'company-participation',
}

export enum FORM_ACTION {
    CREATE = 'create',
    UPDATE = 'update',
    UPDATE_BATCH = 'update_batch',
    UPDATE_BATCH_IN_COMPANY = 'update_batch_in_company',
    CANCEL = 'cancel',
    CANCEL_BATCH = 'cancel_batch',
    CANCEL_BATCH_IN_COMPANY = 'cancel_batch_in_company',
}

export interface IEventFormProps {
    event: Partial<IEvent>;
    resources: IResource[];
    onClose: Function;
    isEditMode: boolean;
}

export interface IAffectedSites {
    company: string;
    company_id?: number;
    site: string;
    site_timezone: string;
    id: string;
}

export interface IEventPeriodDate {
    label: string;
    value: {
        start: number;
        end: number;
    };
}

export class ViewEvent {
    event_id?: number;
    site_id?: number;
    event_cancelled?: boolean;
    event_datetime_start_local?: Date | any;
    event_duration_hours: number;
    no_end_time?: boolean;
    event_emergency_generator_allowed: boolean;
    event_voluntary: boolean;
    event_external_event_id?: number;
    event_market_context?: string;
    event_modification_number?: number;
    event_opt_type?: OptType;
    event_post_duration_hours: number;
    event_pre_duration_hours: number;
    event_secondary_duration_hours: number;
    event_signal?: SignalType;
    event_test: boolean;
    event_datetime_created?: Date;
    event_batch?: string;
    event_slice?: ISlice;
    event_source?: string;
    program_id?: number;
    program_name?: string;
    site_timezone?: string;

    constructor(event: Partial<IEvent>) {
        this.event_signal = event.event_signal;
        this.event_test = !!event.event_test;
        this.event_emergency_generator_allowed = !!event.event_emergency_generator_allowed;
        this.event_voluntary = !!event.event_voluntary;

        if (event.event_datetime_start) {
            const zoneDate = toZonedTime(event.event_datetime_start, event.site!.site_timezone);
            this.event_datetime_start_local = dayjs(zoneDate) || undefined;
        }

        this.event_market_context = event.event_market_context;
        this.event_batch = event.event_batch;
        this.no_end_time = event.no_end_time;

        this.event_duration_hours = minutesToHours(event.event_duration);
        this.event_pre_duration_hours = minutesToHours(event.event_pre_duration);
        this.event_secondary_duration_hours = minutesToHours(event.event_secondary_duration);
        this.event_post_duration_hours = minutesToHours(event.event_post_duration) ?? 0;
        this.event_slice = event.event_slice;
        this.event_source = (event as any).event_source;
        this.program_id = event.program_id;
        this.program_name = (event as any).program_name;
        this.site_timezone = event.site?.site_timezone;
    }
}

export function toBatchRequest(viewEvent: ViewEvent, resources?: IResource[], programId?: number) {
    /** By default, for events with no_end_time we set duration 12h */
    const event_duration = viewEvent.no_end_time ? 12 * 60 : viewEvent.event_duration_hours * 60;

    return {
        ...(resources ? { sites: resources.map(r => +r.id) } : { sites: [] }),
        ...(programId ? { programs: [programId] } : {}),
        event: {
            event_datetime_start_local: viewEvent.event_datetime_start_local && toUniversalTime(viewEvent.event_datetime_start_local),
            event_duration,
            no_end_time: viewEvent.no_end_time,
            event_emergency_generator_allowed: viewEvent.event_emergency_generator_allowed,
            event_voluntary: viewEvent.event_voluntary,
            event_pre_duration: viewEvent.event_pre_duration_hours * 60,

            event_post_duration: viewEvent.event_post_duration_hours * 60,
            event_secondary_duration: viewEvent.event_secondary_duration_hours * 60,
            event_test: viewEvent.event_test,

            event_signal: viewEvent.event_signal,
            event_market_context: viewEvent.event_market_context,
            program_id: programId || viewEvent.program_id,
            ...(viewEvent.event_slice && { event_slice: viewEvent.event_slice }),
        },
    };
}

export const eventViewOptions = (actor: Actor) => {
    const viewOptions = [
        {
            title: 'Calendar View',
            value: VIEW_MODE.CALENDAR,
            tooltip: 'Calendar View',
            icon: <CalendarOutlined />,
            isVisible: true,
        },
        {
            title: 'Historical View',
            value: VIEW_MODE.HISTORY,
            tooltip: 'Historical View',
            icon: <TableOutlined />,
            isVisible: true,
        },
        {
            title: 'Tracking View',
            value: VIEW_MODE.TRACKING,
            tooltip: 'Tracking View',
            icon: <AuditOutlined />,
            isVisible: true,
        },
    ];

    if (actor.isAdminRoleType()) {
        viewOptions.push({
            title: 'Company View',
            value: VIEW_MODE.COMPANY_PARTICIPATION,
            tooltip: 'Company Participation',
            icon: <FileDoneOutlined />,
            isVisible: true,
        });
    }

    return viewOptions;
};
